import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

const Registration = ({ isSaas = false }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isFormLoading, setIsFormLoading] = useState(true);
    const [feedback, setFeedback] = useState('');
    const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
    const [formFields, setFormFields] = useState([]);
    const navigate = useNavigate();

    // Define form validation schema
    const schema = yup.object().shape({
        name: yup.string().required('Это поле обязательно'),
        surname: yup.string().required('Это поле обязательно'),
        company: yup.string().required('Это поле обязательно'),
        email: yup.string().email('Неверный формат email').required('Это поле обязательно'),
        agreement: yup.boolean()
            .required('Требуется согласие с нашей политикой обработки данных')
            .oneOf([true], 'Требуется согласие с нашей политикой обработки данных'),
        // Hidden fields
        reg_type: yup.string(),
        password: yup.string(),
        repeat_email: yup.string()
    });

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            reg_type: '',
            name: '',
            surname: '',
            company: '',
            email: '',
            password: 'temp_pass',
            repeat_email: '',
            agreement: false
        }
    });

    const agreementValue = watch('agreement');

    useEffect(() => {
        console.log('Agreement value updated:', agreementValue);
    }, [agreementValue]);

    useEffect(() => {
        // Fetch form configuration from API or use static config
        const getFormConfig = async () => {
            setIsFormLoading(true);
            try {
                const response = await fetch('/api/v1/forms/registration');
                if (!response.ok) {
                    throw new Error('Failed to fetch form configuration');
                }
                const data = await response.json();
                console.log(data); // Log the entire response
                
                // Check if data.fields exists and is an array
                if (data && Array.isArray(data.fields)) {
                    console.log('Setting form fields from API:', data.fields);
                    setFormFields(data.fields);
                } else if (data && data.fields) {
                    // If data.fields exists but is not an array, log the error and use default
                    console.error('API returned non-array fields:', data.fields);
                    useDefaultFields();
                } else {
                    // No valid data.fields
                    throw new Error('Invalid form configuration format');
                }
            } catch (error) {
                console.error('Error fetching form configuration:', error);
                useDefaultFields();
            } finally {
                setIsFormLoading(false);
            }
        };

        // Define default fields as a separate function for reuse
        const useDefaultFields = () => {
            console.log('Using default form fields');
            const defaultFields = [
                {
                    fill: true,
                    label: "reg_type",
                    name: "reg_type",
                    type: "hidden"
                },
                {
                    fill: true,
                    label: "Имя",
                    name: "name",
                    placeholder: "Имя",
                    required: "Пожалуйста, укажите Ваше имя",
                    type: "text"
                },
                {
                    fill: true,
                    label: "Фамилия",
                    name: "surname",
                    placeholder: "Фамилия",
                    required: "Пожалуйста, укажите Вашу фамилию",
                    type: "text"
                },
                {
                    fill: true,
                    label: "Компания",
                    name: "company",
                    placeholder: "Компания",
                    required: "Пожалуйста, укажите название Вашей компании",
                    type: "text"
                },
                {
                    fill: false,
                    label: "E-mail",
                    name: "email",
                    placeholder: "E-mail",
                    required: "Пожалуйста, укажите Ваш e-mail",
                    type: "email"
                },
                {
                    fill: true,
                    label: "Пароль",
                    name: "password",
                    placeholder: "",
                    required: "",
                    type: "hidden",
                    value: "temp_pass"
                },
                {
                    fill: true,
                    label: "",
                    name: "repeat_email",
                    placeholder: "email",
                    required: "",
                    type: "hidden"
                },
                {
                    fill: true,
                    label: "",
                    name: "agreement",
                    required: "требуется согласие с нашей политикой обработки данных",
                    type: "agreement"
                }
            ];
            setFormFields(defaultFields);
        };

        getFormConfig();
    }, []);

    const onSubmit = async (data) => {
        setIsLoading(true);
        setSubmitStatus({ type: '', message: '' });

        // Create a copy of the data for API submission
        const apiData = {...data};
        
        // Set agreement value to "on" when checked for API
        apiData.agreement = data.agreement ? "on" : "";

        try {
            const response = await fetch('/api/v1/registration', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(apiData),
            });

            const responseData = await response.json();

            if (response.ok && responseData.success) {
                // Show success in the modal or feedback
                setSubmitStatus({
                    type: 'success',
                    message: 'Регистрация успешна. Пожалуйста, проверьте вашу электронную почту для получения учетных данных.'
                });
                document.getElementById('registration_confirmation_modal').classList.add('show');
                document.getElementById('registration_confirmation_modal').style.display = 'block';
            } else {
                setSubmitStatus({
                    type: 'error',
                    message: responseData.message || 'Ошибка регистрации. Пожалуйста, попробуйте снова.'
                });
            }
        } catch (error) {
            console.error('Registration error:', error);
            setSubmitStatus({
                type: 'error',
                message: 'Произошла ошибка при регистрации. Пожалуйста, попробуйте снова.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const FormField = ({ field, register, error }) => {
        const baseInputClasses = "w-full h-[32px] lg:h-[52px] px-[20px] lg:px-[27px] lg:py-3.5 bg-white rounded-[10px] text-[#544a28] text-[13px] lg:text-base font-normal font-['Open Sans']";

        if (field.type === 'hidden') {
            return (
                <input
                    type="hidden"
                    {...register(field.name)}
                    defaultValue={field.value || ''}
                />
            );
        }

        if (field.type === 'text' || field.type === 'email') {
            return (
                <div className="w-full flex-col justify-start items-start gap-5 inline-flex">
                    <label className="text-[#544a28] text-[13px] lg:text-base font-bold font-['Open Sans']">
                        {field.label}
                    </label>
                    <input
                        type={field.type}
                        {...register(field.name)}
                        placeholder={field.placeholder || ''}
                        className={`${baseInputClasses} ${error ? 'border-red-500 border-2' : ''}`}
                    />
                    {error && <span className="text-red-500 text-sm">{error.message}</span>}
                </div>
            );
        }

        if (field.type === 'agreement') {
            return (
                <div className="w-full xl:w-[670px] flex-col justify-start items-start gap-5 inline-flex">
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="agreement-checkbox"
                            {...register('agreement')}
                            className={`w-4 h-4 ${error ? 'border-red-500 border-2' : ''}`}
                        />
                        <label 
                            htmlFor="agreement-checkbox" 
                            className="text-[#544a28] text-[13px] lg:text-base font-normal font-['Open Sans']"
                        >
                            Я согласен с <a className="text-[#c0834d] hover:underline" href="/agreement">политикой обработки персональных данных</a>
                        </label>
                    </div>
                    {error && <span className="text-red-500 text-sm">{error.message}</span>}
                </div>
            );
        }

        return null;
    };

    return (
        <section className="w-full bg-gray-100 py-10">
            <div className="w-full lg:w-[1024px] xxl:w-[1400px] mx-auto flex-col justify-center items-center gap-10 flex">

                <div className="h-11 text-center bg-gradient-to-r from-[#6082A0] to-[#E55961] text-transparent bg-clip-text leading-normal text-[24px] lg:text-[32px] font-extrabold lg:font-bold font-['Open Sans']">
                    Регистрация
                </div>

                <div className="w-full flex flex-col lg:flex-row gap-10 md:p-5 justify-center items-center">

                    {isFormLoading ? (
                        <div className="flex justify-center items-center p-10 bg-[#ebebeb] rounded-[20px] w-full max-w-[670px]">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#c0834d]"></div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit(
                            (data) => {
                                console.log('Form is valid, submitting with data:', data);
                                onSubmit(data);
                            }, 
                            (errors) => {
                                console.error('Form validation failed:', errors);
                            }
                        )} className="flex-col justify-start items-center gap-10 flex bg-[#ebebeb] p-5 rounded-[20px] w-full max-w-[670px]">
                            {/* Status Message */}
                            {submitStatus.message && (
                                <div className={`w-full p-4 rounded-md ${submitStatus.type === 'success'
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-red-100 text-red-700'
                                    }`}>
                                    {submitStatus.message}
                                </div>
                            )}

                            {/* Registration Form */}
                            <div className="w-full flex-col justify-start items-start gap-5 flex">
                                {/* Ensure formFields is an array before filtering */}
                                {Array.isArray(formFields) && formFields.filter(field => field.type !== 'hidden').map((field, index) => (
                                    <FormField
                                        key={index}
                                        field={field}
                                        register={register}
                                        error={errors[field.name]}
                                    />
                                ))}

                                {/* Hidden Fields */}
                                {Array.isArray(formFields) && formFields.filter(field => field.type === 'hidden').map((field, index) => (
                                    <FormField
                                        key={`hidden-${index}`}
                                        field={field}
                                        register={register}
                                        error={errors[field.name]}
                                    />
                                ))}
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full h-[52px] px-[27px] py-3.5 bg-gradient-to-br from-[#c0834d] to-[#c0834d] rounded-[10px] justify-center items-center gap-2.5 inline-flex text-white text-base font-bold font-['Open Sans'] cursor-pointer disabled:opacity-50"
                            >
                                {isLoading ? 'Регистрация...' : 'Зарегистрироваться'}
                            </button>
                        </form>
                    )}

                    {/* Steps section */}
                    <div className="self-stretch flex-col justify-center items-center gap-10 flex mt-12 w-full max-w-[320px] lg:max-w-[470px]">
                        
                        <div className="w-full flex flex-col h-full pb-[90px] justify-between gap-8">
                           
                           <div className="flex items-center gap-4">
                                <span className="flex justify-center items-center w-9 h-9 min-w-[36px] min-h-[36px] bg-[#c0834d] rounded-full text-white font-bold">
                                    1
                                </span>
                                <div className="text-[#544a28] text-[13px] lg:text-base font-normal font-['Open Sans']">
                                    Заполните форму регистрации
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <span className="flex justify-center items-center w-9 h-9 min-w-[36px] min-h-[36px] bg-[#c0834d] rounded-full text-white font-bold">
                                    2
                                </span>
                                <div className="text-[#544a28] text-[13px] lg:text-base font-normal font-['Open Sans']">
                                    Модерация занимает до 24 часов.
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <span className="flex justify-center items-center w-9 h-9 min-w-[36px] min-h-[36px] bg-[#c0834d] rounded-full text-white font-bold">
                                    3
                                </span>
                                <div className="text-[#544a28] text-[13px] lg:text-base font-normal font-['Open Sans']">
                                    Мы отправим ваш логин/пароль от личного кабинета на указанную почту.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirmation Modal */}
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 hidden" id="registration_confirmation_modal">
                <div className="bg-white p-8 rounded-lg w-[90%] max-w-md">
                    <div className="text-center">
                        <div className="text-[#544a28] text-base font-bold mb-4">
                            {submitStatus.message}
                        </div>
                        <button
                            className="px-10 h-[45px] bg-[#c0834d] rounded-[20px] text-white text-lg font-bold"
                            type="button"
                            onClick={() => {
                                document.getElementById('registration_confirmation_modal').classList.remove('show');
                                document.getElementById('registration_confirmation_modal').style.display = 'none';
                            }}
                        >
                            OK
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Registration;